/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatTabsModule } from "@angular/material/tabs";
import { Router, RouterOutlet } from "@angular/router";
import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { EChartsType } from "echarts";
import { BehaviorSubject, defer, finalize, map, Observable, Subject, takeUntil, tap } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DatepickerComponent } from "../../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { SwitchComponent } from "../../../shared/components/switch/switch.component";
import { PaginationComponent } from "../../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import {
	AdvancedFilters,
	EnvironmentalImpactByApplication
} from "../../../shared/models/http/application/environmental-impact-by-application.model";
import { LineChart } from "../../../shared/models/http/charts/line-chart/line-chart.model";
import { FilterRequest } from "../../../shared/models/http/dashboard/filter-page-response.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { KPI } from "../../../shared/models/http/dashboard/kpi.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { PaginatedSelectionModel } from "../../../shared/models/ui/paginated-selection.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { SoftwareAssetsGlobalForm } from "../../../shared/models/ui/software-assets-global-form.model";
import { TimePeriodRange } from "../../../shared/models/ui/time-period-range.model";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DashboardSoftwareAssetsService } from "../../../shared/services/dashboard-software-assets.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { DashboardStateService } from "../../../shared/services/state/dashboard-state.service";
import { StorageService } from "../../../shared/services/storage.service";
import { ToastService } from "../../../shared/services/toast.service";
import { COLORS, PATHS } from "../../../shared/utils/constants";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../shared/utils/lazy-load";
import { getCurrentWeekRange } from "../../../shared/utils/reporting-period";
import {
	convertTabLabelToRoute,
	formatDateForBE,
	formatNumberMaxDecimals,
	saveAs
} from "../../../shared/utils/utils";

@Component({
	selector: "globalictcc-software-assets",
	standalone: true,
	imports: [
		AsyncPipe,
		RouterOutlet,
		MatTabsModule,
		DatepickerComponent,
		DropdownComponent,
		ButtonComponent,
		ChartComponent,
		ChipsComponent,
		SwitchComponent,
		ReactiveFormsModule,
		KpiComponent,
		TableComponent,
		PaginationComponent
	],
	templateUrl: "./software-assets.component.html",
	styleUrl: "./software-assets.component.scss"
})
export class SoftwareAssetsComponent implements OnInit, OnDestroy {
	// ── Session-storage keys for pagination cursors ──────────────────────────
	private static readonly _swAppsCursorKey = "sw-apps-pagination-cursor";
	private static readonly _swLayersCursorKey = "sw-layers-pagination-cursor";
	private static readonly _pageSize = 20;

	public configurationForm = new FormGroup({
		applications: new FormControl<string[]>([], [Validators.required]),
		layers: new FormControl<string[]>([], [Validators.required]),
		timePeriod: new FormControl<TimePeriodRange>(getCurrentWeekRange(), [Validators.required])
	});

	public chips: ChipOption[] = [
		{ label: "ENVIRONMENTAL IMPACT", value: "ENVIRONMENTAL IMPACT" },
		{ label: "COMPONENT EMISSIONS", value: "COMPONENT EMISSIONS" },
		{ label: "SOFTWARE CARBON INTENSITY", value: "SOFTWARE CARBON INTENSITY" },
		{ label: "ENERGY CONSUMPTION", value: "ENERGY CONSUMPTION" }
	];

	public applicationOptions: SelectOption[];
	public layerOptions: SelectOption[];
	public readonly grants = GRANTS.swart;
	public isManifestDownloadInProgress: boolean = false;

	// ── Paginated-dropdown state ──────────────────────────────────────────────
	public hasMoreApplications: boolean = false;
	public hasMoreLayers: boolean = false;
	public isApplicationsLoadingMore: boolean = false;
	public isLayersLoadingMore: boolean = false;

	/**
	 * Semantic selection model for the Application ID dropdown.
	 * Used when calling the backend so that "Select All" does not require sending
	 * thousands of IDs — instead the backend receives `{ allSelected: true, excludedIds: [] }`.
	 */
	public applicationsSelectionModel: PaginatedSelectionModel = {
		allSelected: true,
		selectedIds: [],
		excludedIds: []
	};

	/**
	 * Semantic selection model for the Layer ID dropdown.
	 * Same contract as `applicationsSelectionModel`.
	 */
	public layersSelectionModel: PaginatedSelectionModel = {
		allSelected: true,
		selectedIds: [],
		excludedIds: []
	};

	public viewSwitchOptions: SelectOption[] = [
		{ label: "Baseline view", value: "upload" },
		{ label: "Realtime view", value: "realtime", selected: true }
	];

	public environmentalImpactCurrentPage: number = 0;
	public readonly environmentalImpactPageSize: number = 10;
	public environmentalImpactTotalItems: number = 0;
	public environmentalImpactTotalPages: number = 1;
	public environmentalImpactPageItems: number = 0;
	public environmentalImpactGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Application", field: "application", flex: 4, maxWidth: 5000 },
			{ headerName: "# of layers", field: "numberOfLayers" },
			{
				headerName: "kgCO2e",
				field: "totalEmissions",
				valueFormatter: params => formatNumberMaxDecimals(params.value, 2)
			}
		],
		rowData: [],
		getRowStyle: params => {
			if (!params?.data?.total) return undefined;
			const percentage = (params.data.totalEmissions / params.data.total) * 100;
			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onEnvironmentalImpactGridReady.bind(this)
	};
	private _environmentalImpactGridApi?: GridApi;
	private _allEnvironmentalImpactRows: {
		application: string;
		numberOfLayers: number;
		totalEmissions: number;
		total: number;
	}[] = [];
	private readonly _document: Document = inject(DOCUMENT);
	private _isFirstRender: boolean = true;
	private _isApplicationDropdownOpen: boolean = false;
	private _tenantId: string;
	private _destroy$ = new Subject<void>();

	// ── Pagination state ──────────────────────────────────────────────────────
	private _applicationsCursor: string | null = null;
	private _layersCursor: string | null = null;
	private _lastApplicationSearchTerm: string = "";
	private _lastLayerSearchTerm: string = "";
	private _currentSelectedApplications: string[] = [];

	private readonly _filters$ = new BehaviorSubject<AdvancedFilters | undefined>(undefined);
	private readonly _appsPerformanceRefresh$ = new Subject<void>();
	private readonly _environmentalImpactRefresh$ = new Subject<void>();
	private _latestEnvironmentalImpact?: Loadable<EnvironmentalImpactByApplication>;

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly totalCO2$: Observable<Loadable<KPI>> = lazyLoad(this._filters$, filters =>
		this._dashboardSoftwareAssetsService.getTotalCO2(filters)
	);

	public readonly appsPerformanceChart$: Observable<Loadable<ChartResponse<LineChart>>> =
		lazyLoad(
			this._filters$,
			filters => this._dashboardSoftwareAssetsService.getAppsPerformance(filters),
			this._appsPerformanceRefresh$
		);

	public readonly environmentalImpact$: Observable<Loadable<EnvironmentalImpactByApplication>> =
		lazyLoad(
			this._filters$,
			filters =>
				this._dashboardSoftwareAssetsService.getEnvironmentalImpactByApplication(filters),
			this._environmentalImpactRefresh$
		).pipe(tap(state => this._applyEnvironmentalImpact(state)));

	constructor(
		private _router: Router,
		private _storageService: StorageService,
		private _dashboardSoftwareAssetsService: DashboardSoftwareAssetsService,
		private _ictCarbonInsightsReportService: IctCarbonInsightsReportService,
		private _dashboardStateService: DashboardStateService,
		private _dialogService: DialogService,
		private _toastService: ToastService,
		public authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		// Restore pagination cursors from the previous session
		this._applicationsCursor =
			sessionStorage.getItem(SoftwareAssetsComponent._swAppsCursorKey) ?? null;
		this._layersCursor =
			sessionStorage.getItem(SoftwareAssetsComponent._swLayersCursorKey) ?? null;

		this._onFormChange();

		this.chips = this.chips.map(chip => ({
			...chip,
			selected: this._router.url.includes(convertTabLabelToRoute(chip.value.toString()))
		}));

		this._fetchApplicationIds("", null);
		this._fetchDashboardData();
	}

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	// ── Public handlers wired to the two paginated dropdowns ─────────────────

	/** Called by the Application-ID dropdown on every debounced search-term change. */
	public onApplicationSearch(term: string): void {
		this._applicationsCursor = null;
		sessionStorage.removeItem(SoftwareAssetsComponent._swAppsCursorKey);
		this._fetchApplicationIds(term, null);
	}

	/** Called by the Application-ID dropdown when scroll reaches the bottom. */
	public onApplicationLoadMore(): void {
		if (!this.hasMoreApplications || this.isApplicationsLoadingMore) return;
		this._fetchApplicationIds(this._lastApplicationSearchTerm, this._applicationsCursor, true);
	}

	/** Called by the Application-ID dropdown when the selection model changes. */
	public onApplicationsSelectionChange(model: PaginatedSelectionModel): void {
		this.applicationsSelectionModel = model;
	}

	/** Called when the Application-ID dropdown panel opens or closes. */
	public onApplicationDropdownOpenedChange(isOpen: boolean): void {
		this._isApplicationDropdownOpen = isOpen;
		if (!isOpen) {
			// Panel closed: fetch layers once with the final application selection.
			this._fetchLayerIds("", null, this._currentSelectedApplications);
		}
	}

	/** Called by the Layer-ID dropdown on every debounced search-term change. */
	public onLayerSearch(term: string): void {
		this._layersCursor = null;
		sessionStorage.removeItem(SoftwareAssetsComponent._swLayersCursorKey);
		this._fetchLayerIds(term, null, this._currentSelectedApplications);
	}

	/** Called by the Layer-ID dropdown when scroll reaches the bottom. */
	public onLayerLoadMore(): void {
		if (!this.hasMoreLayers || this.isLayersLoadingMore) return;
		this._fetchLayerIds(
			this._lastLayerSearchTerm,
			this._layersCursor,
			this._currentSelectedApplications,
			true
		);
	}

	/** Called by the Layer-ID dropdown when the selection model changes. */
	public onLayersSelectionChange(model: PaginatedSelectionModel): void {
		this.layersSelectionModel = model;
	}

	/**
	 * Handles the selection of a chip and navigates to the corresponding route.
	 *
	 * @param selectedChip - The selected chip, which can be a string or a number.
	 */
	public handleSelection(selectedChip: string | number) {
		this._router.navigateByUrl(
			`${PATHS.DASHBOARD}/${PATHS.SOFTWARE_ASSETS}/${convertTabLabelToRoute(selectedChip.toString())}`
		);
	}

	public onViewSwitch(targetView: string): void {
		if (targetView === "upload") {
			this._router.navigateByUrl(`${PATHS.DASHBOARD}/${PATHS.STRATEGIC_APPLICATIONS}`);
		}
	}

	/**
	 * Handles changes to the configuration form.
	 *
	 * This method subscribes to the `valueChanges` observable of the `configurationForm` and performs
	 * the following actions when the form changes:
	 * - Updates the global form state in the `_dashboardStateService`.
	 * - Filters and maps the applications to update the `layerOptions` based on the selected applications.
	 * - On the first render, sets the `layers` control value to include all available layer options.
	 *
	 * The subscription is automatically unsubscribed when the component is destroyed using the `_destroy$` observable.
	 *
	 * @private
	 */
	private _onFormChange() {
		this.configurationForm.controls.applications.valueChanges
			.pipe(
				tap({
					next: applications => {
						const previousLayers = this.configurationForm.controls.layers.value;

						this._currentSelectedApplications = applications ?? [];

						// Reset layer cursor whenever the application selection changes
						this._layersCursor = null;
						this._lastLayerSearchTerm = "";
						sessionStorage.removeItem(SoftwareAssetsComponent._swLayersCursorKey);

						// Fetch first page of layers only when the application panel is not open
						// (i.e. on initial load or programmatic change). When the panel is open the
						// fetch is deferred to onApplicationDropdownOpenedChange(false) so that a
						// single API call is made after the user finishes selecting.
						if (!this._isApplicationDropdownOpen) {
							this._fetchLayerIds("", null, this._currentSelectedApplications);
						}

						// Keep layers that are still valid after the application change
						const newLayers = (previousLayers || []).filter(layer =>
							this.layerOptions.some(option => option.value === layer)
						);
						this.configurationForm.controls.layers.setValue(newLayers);

						if (this._isFirstRender) {
							this._isFirstRender = false;
							this.configurationForm.controls.layers.setValue(
								this.layerOptions.map(option => option.value)
							);
						}
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the apply action by fetching the dashboard data.
	 * This method is triggered when the user applies a certain action
	 * on the dashboard.
	 *
	 * @private
	 */
	public onApply() {
		this._fetchDashboardData();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	/**
	 * Fetches the dashboard data including total CO2 emissions and application performance.
	 *
	 * This method retrieves form options and uses them to request total CO2 emissions and
	 * application performance data from the dashboard general service. The results are
	 * combined and subscribed to update the component's state with the fetched data.
	 *
	 * @private
	 */
	private _fetchDashboardData() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		this._dashboardStateService.setSoftwareAssetsGlobalForm({
			...(this.configurationForm.value as unknown as SoftwareAssetsGlobalForm),
			applicationsSelection: this.applicationsSelectionModel,
			layersSelection: this.layersSelectionModel
		});

		this._filters$.next(this._buildAdvancedFilters(formOptions.startDate, formOptions.endDate));
	}

	public onEnvironmentalImpactTableRefresh(): void {
		this._environmentalImpactRefresh$.next();
	}

	public onEnvironmentalImpactNextPage(): void {
		this.environmentalImpactCurrentPage++;
		this._displayCurrentEnvironmentalImpactPage();
	}

	public onEnvironmentalImpactPreviousPage(): void {
		this.environmentalImpactCurrentPage--;
		this._displayCurrentEnvironmentalImpactPage();
	}

	private _onEnvironmentalImpactGridReady(params: GridReadyEvent): void {
		this._environmentalImpactGridApi = params.api;
		if (this._latestEnvironmentalImpact) {
			this._applyEnvironmentalImpact(this._latestEnvironmentalImpact);
		}
	}

	// The grid is driven through its API, so each state is pushed to it here.
	private _applyEnvironmentalImpact(state: Loadable<EnvironmentalImpactByApplication>): void {
		this._latestEnvironmentalImpact = state;

		const gridApi = this._environmentalImpactGridApi;
		if (!gridApi || gridApi.isDestroyed()) return;

		gridApi.setGridOption("loading", state.isLoading);
		if (state.isLoading) return;

		this.environmentalImpactCurrentPage = 0;
		const total = state.data?.totalEmission ?? 0;
		this._allEnvironmentalImpactRows = (state.data?.contents ?? []).map(row => ({
			...row,
			total
		}));
		this.environmentalImpactTotalItems = this._allEnvironmentalImpactRows.length;
		this.environmentalImpactTotalPages = Math.max(
			1,
			Math.ceil(this._allEnvironmentalImpactRows.length / this.environmentalImpactPageSize)
		);
		this._displayCurrentEnvironmentalImpactPage();
	}

	private _displayCurrentEnvironmentalImpactPage(): void {
		const start = this.environmentalImpactCurrentPage * this.environmentalImpactPageSize;
		const pageData = this._allEnvironmentalImpactRows.slice(
			start,
			start + this.environmentalImpactPageSize
		);
		this.environmentalImpactPageItems = pageData.length;
		this._environmentalImpactGridApi?.setGridOption("rowData", pageData);
	}

	/**
	 * Initiates the export of a report in CSV format based on the current form options.
	 * If the form options are not available, the export process is aborted.
	 *
	 * The export process involves the following steps:
	 * 1. Retrieve the form options including start date, end date, applications, and layers.
	 * 2. Call the exportResponseInCsv method of the dashboard general service with the retrieved options and tenant ID.
	 * 3. Subscribe to the export response to handle the CSV export process.
	 */
	public onDownloadAsCSV() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		defer(() => {
			this._toastService.success(
				"Download started",
				"Your CSV download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._dashboardSoftwareAssetsService.exportResponseInCsv(
				this._buildAdvancedFilters(formOptions.startDate, formOptions.endDate)
			);
		})
			.pipe(
				map(blob => URL.createObjectURL(blob)),
				tap({
					next: (url: string) => {
						(saveAs(
							this._document,
							url,
							`${environment.dashboardExportFileName}_${new Date().toLocaleDateString()}.csv`
						),
							URL.revokeObjectURL(url)); // Free the memory
					}
				})
			)
			.subscribe();
	}

	public onDownloadManifest() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		if (formOptions.applications.length > 1) {
			this._dialogService.open({
				data: {
					type: "info",
					closeBtnText: "Close",
					title: "Warning",
					message: `You can download at least one Manifest file per application: Select one application from the filters`
				}
			});
			return;
		}

		this.isManifestDownloadInProgress = true;

		this._dashboardSoftwareAssetsService
			.exportResponseManifest(
				formOptions.startDate,
				formOptions.endDate,
				formOptions.applications,
				this._tenantId
			)
			.pipe(
				map((yamlBlob: Blob) => {
					const blob = yamlBlob.type
						? yamlBlob
						: new Blob([yamlBlob], { type: "text/yaml;charset=utf-8" });
					return URL.createObjectURL(blob);
				}),
				tap({
					next: (url: string) => {
						const date = new Date().toLocaleDateString();
						saveAs(this._document, url, `OMP_${date}.yaml`);
					}
				}),
				finalize(() => {
					this.isManifestDownloadInProgress = false;
				})
			)
			.subscribe();
	}

	/**
	 * Initiates the export of a report in PDF format based on the current form options.
	 *
	 * This method retrieves the form options, constructs the report parameters,
	 * and calls the dashboard general service to export the report. The export
	 * process is terminated if the component is destroyed.
	 *
	 * @returns This method does not return a value.
	 */
	public onExportReport() {
		const formOptions = this._getFormOptions();

		if (!formOptions) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.APPLICATIONS, {
				from: formOptions.startDate,
				to: formOptions.endDate
			})
			.subscribe();
	}

	/**
	 * Handles the chart refresh event by reloading the applications performance data.
	 */
	public onChartRefresh() {
		this._appsPerformanceRefresh$.next();
	}

	/**
	 * Retrieves the form options from the configuration form.
	 *
	 * This method extracts the `applications`, `layers`, and `timePeriod` values from the form.
	 * It ensures that all necessary values are present and that the `timePeriod` contains valid
	 * `start` and `end` dates. If any of these conditions are not met, the method returns `undefined`.
	 *
	 * If all conditions are met, it formats the `start` and `end` dates to the 'YYYY-MM-DD' format
	 * and returns an object containing the `applications`, `layers`, `startDate`, and `endDate`.
	 *
	 * @returns An object containing the form options or `undefined` if any required value is missing.
	 */
	private _buildAdvancedFilters(startDate: string, endDate: string): AdvancedFilters {
		const applicationsSelection = this.applicationsSelectionModel;
		const layersSelection = this.layersSelectionModel;
		return {
			startDate,
			endDate,
			allApplicationsSelected: applicationsSelection.allSelected,
			allLayersSelected: layersSelection.allSelected,
			applicationsSelection: {
				selectedIds: applicationsSelection.selectedIds,
				excludedIds: applicationsSelection.excludedIds
			},
			layersSelection: {
				selectedIds: layersSelection.selectedIds,
				excludedIds: layersSelection.excludedIds
			}
		};
	}

	private _getFormOptions() {
		const { applications, layers, timePeriod } = this.configurationForm.value;

		if (!applications || !layers || !timePeriod?.start || !timePeriod?.end) return;

		const { start, end } = timePeriod;

		const startDate = formatDateForBE(start);
		const endDate = formatDateForBE(end);

		if (!startDate || !endDate) return;

		return {
			applications,
			layers,
			startDate,
			endDate,
			applicationsSelection: this.applicationsSelectionModel,
			layersSelection: this.layersSelectionModel
		};
	}

	// ── Paginated fetchers ──────────────────────────────────────────────────────

	/**
	 * Fetches a page of Application IDs from the filter API.
	 */
	private _fetchApplicationIds(
		searchTerm: string,
		cursor: string | null,
		isLoadingMore: boolean = false
	): void {
		if (!isLoadingMore) {
			this.applicationOptions = [];
		}
		this.isApplicationsLoadingMore = true;
		this._lastApplicationSearchTerm = searchTerm;

		this._dashboardSoftwareAssetsService
			.getApplicationsIds(searchTerm, cursor ?? null, SoftwareAssetsComponent._pageSize)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => (this.isApplicationsLoadingMore = false))
			)
			.subscribe(response => {
				const mapped = response.content.map(app => ({ label: app.name, value: app.name }));
				this.applicationOptions = isLoadingMore
					? [...this.applicationOptions, ...mapped]
					: mapped;
				this.hasMoreApplications = response.hasNext;
				this._applicationsCursor = response.nextCursor ?? null;
				if (this._applicationsCursor != null) {
					sessionStorage.setItem(
						SoftwareAssetsComponent._swAppsCursorKey,
						this._applicationsCursor
					);
				} else {
					sessionStorage.removeItem(SoftwareAssetsComponent._swAppsCursorKey);
				}
			});
	}

	private _fetchLayerIds(
		searchTerm: string,
		cursor: string | null,
		selectedApplications: string[],
		isLoadingMore: boolean = false
	): void {
		if (!isLoadingMore) {
			this.layerOptions = [];
		}
		this.isLayersLoadingMore = true;
		this._lastLayerSearchTerm = searchTerm;

		const body: FilterRequest = {
			allSelected: this.applicationsSelectionModel.allSelected,
			selectedIds: this.applicationsSelectionModel.allSelected
				? undefined
				: selectedApplications,
			excludedIds: this.applicationsSelectionModel.allSelected
				? this.applicationsSelectionModel.excludedIds
				: undefined
		};

		this._dashboardSoftwareAssetsService
			.getLayerIds(body, searchTerm, cursor ?? null, SoftwareAssetsComponent._pageSize)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => (this.isLayersLoadingMore = false))
			)
			.subscribe(response => {
				const mapped = response.content.map(layer => ({ label: layer, value: layer }));
				this.layerOptions = isLoadingMore ? [...this.layerOptions, ...mapped] : mapped;
				this.hasMoreLayers = response.hasNext;
				this._layersCursor = response.nextCursor ?? null;
				if (this._layersCursor != null) {
					sessionStorage.setItem(
						SoftwareAssetsComponent._swLayersCursorKey,
						this._layersCursor
					);
				} else {
					sessionStorage.removeItem(SoftwareAssetsComponent._swLayersCursorKey);
				}
			});
	}
}
